import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { auth, ApiError } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";
import { AuthShell, Field, inputCls, primaryBtn } from "./auth.sign-in";
import { AccountPanel } from "~/components/account/account-panel";
const uuid = (v: unknown) =>
  typeof v === "string" && /^[0-9a-f-]{36}$/i.test(v) ? v : undefined;
export const Route = createFileRoute("/desktop/account")({
  validateSearch: (raw) => ({
    user: uuid(raw.user),
    workspace: uuid(raw.workspace),
    action:
      typeof raw.action === "string" &&
      ["account", "invite", "add-workspace", "security"].includes(raw.action)
        ? raw.action
        : "account",
  }),
  component: DesktopAccount,
});
function DesktopAccount() {
  const { user, workspace, action } = Route.useSearch();
  const store = useAuthStore();
  const [email, setEmail] = useState(""),
    [password, setPassword] = useState(""),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function signIn(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await auth.signIn({ email, password });
      setPassword("");
      await store.setUser(u);
      setError("");
    } catch (e) {
      setError(
        e instanceof ApiError
          ? e.message
          : "Sign-in unavailable. Retry when connected.",
      );
    } finally {
      setBusy(false);
    }
  }
  async function signOut() {
    setBusy(true);
    try {
      await auth.signOut();
      store.clear();
      setError("");
    } catch {
      setError("Could not sign out. Retry.");
    } finally {
      setBusy(false);
    }
  }
  if (store.status === "authenticated" && (!user || store.user?.id === user))
    return (
      <main className="mx-auto max-w-2xl p-8">
        <a href="jotty://settings/callback" className="mb-8 inline-block">
          ← Return to Jotty for Mac
        </a>
        <AccountPanel workspaceId={workspace} action={action} />
      </main>
    );
  return (
    <AuthShell
      title="Your Jotty account"
      subtitle="Account changes happen in your browser. Return to the desktop app when finished."
    >
      {store.status === "unknown" || store.status === "loading" ? (
        <p>Checking your session…</p>
      ) : store.status === "authenticated" ? (
        <>
          <p>
            This browser is signed in as {store.user?.email}. Use the same Jotty
            account as your Mac to manage its workspace.
          </p>
          <button
            className={primaryBtn}
            disabled={busy}
            onClick={() => void signOut()}
          >
            Use the desktop account
          </button>
        </>
      ) : (
        <form className="flex flex-col gap-4" onSubmit={signIn}>
          <Field label="Email">
            <input
              className={inputCls}
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
          </Field>
          <Field label="Password">
            <input
              className={inputCls}
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </Field>
          <button disabled={busy} className={primaryBtn}>
            Sign in
          </button>
        </form>
      )}
      {error && <p role="alert">{error}</p>}
      <a href="jotty://settings/callback">Return to Jotty</a>
    </AuthShell>
  );
}
